import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import DynamicTable from "../../../../components/lab-dynamic-table";
import DeleteConfirmationModal from "../../../../components/Modals/delete-modal";

const CollectionCenter = () => {
  const [editData, setEditData] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [total, setTotal] = useState<any>("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [centersData, setCentersData] = useState<any>([]);

  const [formData, setFormData] = useState({
    collectionCenterName: "",
    descripiton: "",
    address: "",
    mobileNumber: "",
    email: "",
    share: "100",
  });

  const emptyFormData = {
    collectionCenterName: "",
    descripiton: "",
    address: "",
    mobileNumber: "",
    email: "",
    share: "100",
  };

  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchInput]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === "mobileNumber") {
      // Remove all non-numeric characters
      const numericValue = value.replace(/\D/g, "");

      // Apply Pakistan mobile number format: 03XX-XXXXXXX
      let formattedValue = "";
      if (numericValue.length > 0) {
        formattedValue = numericValue.substring(0, 4);
        if (numericValue.length > 4) {
          formattedValue += "-" + numericValue.substring(4, 11);
        }
      }

      setFormData(prevState => ({
        ...prevState,
        [name]: formattedValue,
      }));
    } else if (name === "share") {
      // Allow only numbers with up to 2 decimal places
      if (value === "" || /^\d{0,3}(\.\d{0,2})?$/.test(value)) {
        setFormData(prevState => ({
          ...prevState,
          [name]: value,
        }));
      }
    } else {
      setFormData(prevState => ({
        ...prevState,
        [name]: value,
      }));
    }
  };

  // Email validation function
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Mobile number validation function
  const isValidMobileNumber = (mobile: string) => {
    // Check if it matches the format 03XX-XXXXXXX (12 characters including dash)
    const mobileRegex = /^03\d{2}-\d{7}$/;
    return mobileRegex.test(mobile);
  };

  // Share validation function (optional, 0 - 100)
  const isValidShare = (share: string) => {
    if (share === "") return true;
    const value = Number(share);
    return !isNaN(value) && value >= 0 && value <= 100;
  };
  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_LAB_URL}/collection-center/getAllByPagination`,
        {
          params: {
            pageNo: page + 1,
            count: rowsPerPage,
            search: searchInput,
          },
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setCentersData(response.data?.data);
      setTotal(response.data?.totalCount);
    } catch (error:any) {
      console.error("Error fetching data:", error);
      toast.error(error?.response.data.message || "Error fetching data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    // Prepare data for API (remove formatting from mobile number)
    const apiData = {
      ...formData,
      mobileNumber: formData.mobileNumber.replace(/-/g, ""), // Remove dash for API
      share:
        formData.share === ""
          ? null
          : Number(formData.share),
    };

    if (editData) {
      try {
        const response = await axios.patch(
          `${process.env.REACT_APP_LAB_URL}/collection-center/updateById/${editData}`,
          apiData,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (!response?.data?.isSuccess) {
          toast.error(response?.data?.message);
        } else {
          toast.success(response?.data?.message);
          setFormData(emptyFormData);
          fetchData();
          setEditData(null);
        }
      } catch (error: any) {
        toast.error(error.message || "Something went wrong");
      }
    } else {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_LAB_URL}/collection-center/addCollectionCenter`,
          apiData,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (!response?.data?.isSuccess) {
          toast.error(response?.data?.message);
        } else {
          toast.success(response?.data?.message);
          setFormData(emptyFormData);
          fetchData();
        }
      } catch (error: any) {
        toast.error(error.message || "Something went wrong");
      }
    }
  };

  const handleEditClick = (id: any, rowData: any) => {
    setEditData(id);

    // Format mobile number for display (add dash if not present)
    const formatMobileForDisplay = (mobile: string) => {
      if (!mobile) return "";
      const numericOnly = mobile.replace(/\D/g, "");
      if (numericOnly.length >= 4) {
        return numericOnly.substring(0, 4) + "-" + numericOnly.substring(4, 11);
      }
      return numericOnly;
    };

    setFormData({
      collectionCenterName: rowData?.collectionCenterName || "",
      descripiton: rowData?.descripiton || "",
      address: rowData?.address || "",
      mobileNumber: formatMobileForDisplay(rowData?.mobileNumber || ""),
      email: rowData?.email || "",
      share:
        rowData?.share === null || rowData?.share === undefined
          ? "100"
          : String(rowData.share),
    });
  };

  const handleDeleteClick = (id: string) => {
    setIsDeleteModalOpen(true);
    setSelectedItemId(id);
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("Authorization token is missing");
        return;
      }

      const response = await axios.delete(
        `${process.env.REACT_APP_LAB_URL}/collection-center/deleteById/${selectedItemId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      fetchData();
      toast.success(response.data.message);
      setFormData(emptyFormData);
      setEditData(null);
    } catch (error: any) {
      toast.error(error.message);
    }
    setIsDeleteModalOpen(false);
  };

  const handleDeleteModalClose = () => {
    setIsDeleteModalOpen(false);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, debouncedSearch]);

  return (
    <Box>
      <Box
        sx={{
          borderRadius: 2,
          padding: "1px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: {
              xs: "1rem",
              sm: "1.25rem",
              md: "1.5rem",
            },
          }}
        >
          Collection Center
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <TextField
            label="Search"
            placeholder="Search Center Name, Address, or Email..."
            variant="outlined"
            value={searchInput}
            className="custom-input"
            onChange={handleSearchInputChange}
            size="small"
            InputProps={{
              endAdornment: (
                <IconButton>
                  <SearchIcon sx={{ color: "#055FFC" }} />
                </IconButton>
              ),
            }}
          />
        </Box>
      </Box>

      <Grid container spacing={1}>
        <Grid item xs={12} sm={4} md={4}>
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: 2,
              p: 3,
              boxShadow: 1,
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
              {editData
                ? "Update Collection Center"
                : "Add New Collection Center"}
            </Typography>

            {/* Row 1: Collection Center Name & Description */}
            <Grid container spacing={1} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={12}>
                <TextField
                  size="small"
                  className="custom-input"
                  label="Center Name"
                  name="collectionCenterName"
                  value={formData.collectionCenterName}
                  fullWidth
                  required
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <TextField
                  size="small"
                  label="Description"
                  className="custom-input"
                  name="descripiton"
                  value={formData.descripiton}
                  fullWidth
                  required
                  onChange={handleChange}
                />
              </Grid>
            </Grid>

            {/* Row 2: Address (full width) */}
            <TextField
              size="small"
              label="Address"
              className="custom-input"
              name="address"
              value={formData.address}
              fullWidth
              required
              onChange={handleChange}
              sx={{ mb: 2 }}
            />

            {/* Row 3: Mobile Number & Email*/}
            <Grid container spacing={1} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  label="Mobile Number"
                  className="custom-input"
                  name="mobileNumber"
                  value={formData.mobileNumber}
                  fullWidth
                  required
                  onChange={handleChange}
                  placeholder="0300-1234567"
                  inputProps={{
                    maxLength: 12, // 11 digits + 1 dash
                  }}
                  error={
                    !!(
                      formData.mobileNumber &&
                      !isValidMobileNumber(formData.mobileNumber)
                    )
                  }
                  helperText={
                    formData.mobileNumber &&
                    !isValidMobileNumber(formData.mobileNumber)
                      ? "Please enter a valid mobile number (e.g., 0300-1234567)"
                      : ""
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  label="Email"
                  className="custom-input"
                  name="email"
                  type="email"
                  value={formData.email}
                  fullWidth
                  required
                  onChange={handleChange}
                  error={!!(formData.email && !isValidEmail(formData.email))}
                  helperText={
                    formData.email && !isValidEmail(formData.email)
                      ? "Please enter a valid email address"
                      : ""
                  }
                />
              </Grid>
            </Grid>

            {/* Row 4: Share in %age */}
            <Grid container spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <Grid item xs={5} sm={5}>
                <TextField
                  size="small"
                  label="Share in %age"
                  className="custom-input"
                  name="share"
                  value={formData.share}
                  fullWidth
                  onChange={handleChange}
                  placeholder="e.g., 10"
                  error={!isValidShare(formData.share)}
                  helperText={
                    !isValidShare(formData.share)
                      ? "Share must be between 0 and 100"
                      : ""
                  }
                />
              </Grid>
              <Grid item xs={7} sm={7}>
                <Typography
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.8rem",
                    lineHeight: 1.3,
                  }}
                >
                  {`It's ${
                    formData.collectionCenterName?.trim() || "<collection center>"
                  } share`}
                </Typography>
              </Grid>
            </Grid>

            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleSubmit}
              disabled={
                !formData.collectionCenterName ||
                !formData.descripiton ||
                !formData.address ||
                !formData.mobileNumber ||
                !formData.email ||
                !isValidMobileNumber(formData.mobileNumber) ||
                !isValidEmail(formData.email) ||
                !isValidShare(formData.share)
              }
            >
              {editData ? "Update" : "Save"}
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12} sm={8} md={8}>
          <DynamicTable
            headers={[
              "SN",
              "Center Name",
              "Description",
              "Address",
              "Mobile",
              "Email",
              "Share (%)",
              "Actions",
            ]}
            data={centersData?.map((item: any, index: any) => [
              page * rowsPerPage + index + 1,
              item?.collectionCenterName || "",
              item?.descripiton || "",
              item?.address || "",
              item?.mobileNumber || "",
              item?.email || "",
              item?.share === null || item?.share === undefined
                ? ""
                : item.share,
            ])}
            loading={loading}
            page={page}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRows={total}
            actionButtons={[
              {
                icon: <EditIcon sx={{ color: "#FFB800", cursor: "pointer" }} />,
                onClick: (rowIndex: any) =>
                  handleEditClick(
                    centersData[rowIndex]?._id,
                    centersData[rowIndex]
                  ),
              },
              {
                icon: (
                  <DeleteIcon sx={{ color: "#FF5454", cursor: "pointer" }} />
                ),
                onClick: (rowIndex: any) =>
                  handleDeleteClick(centersData[rowIndex]?._id),
              },
            ]}
            height={"60vh"}
          />
          {centersData === null && (
            <Typography
              sx={{
                color: "gray",
                marginTop: "8px",
                textAlign: "center",
                fontSize: "12px",
                fontWeight: "bold",
                marginBottom: "8px",
              }}
            >
              No data found
            </Typography>
          )}
        </Grid>
      </Grid>

      <DeleteConfirmationModal
        open={isDeleteModalOpen}
        handleClose={handleDeleteModalClose}
        handleConfirm={handleDeleteConfirm}
      />
    </Box>
  );
};

export default CollectionCenter;
